import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiShield, FiLock, FiActivity, FiDownload } from 'react-icons/fi';
import DotGrid from './DotGrid';
// Public image paths for better performance and preloading
const image2 = '/images/Signing-document.webp';
const image3 = '/images/E-stampLatest.webp';
const image4 = '/images/HeroSectionPhoto2Latest.webp';
const image5 = '/images/StampSolv_GodTier.webp';
const image6 = '/images/SignSolv_GodTier.webp';
const image7 = '/images/DoqSolv_GodTier.webp';
const image8 = '/images/KYCSolv_GodTier.webp';
const vendorImage = '/images/vendor_handshake_warm.webp';

// ========== CHRISTMAS DECORATIONS - START ==========

// ========== CHRISTMAS DECORATIONS - END ==========

const TypingHeading = React.memo(({ industries }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);
  const [blink, setBlink] = useState(true);

  useEffect(() => {
    const cursorBlink = setInterval(() => setBlink(b => !b), 500);
    return () => clearInterval(cursorBlink);
  }, []);

  useEffect(() => {
    const currentText = industries[currentIndex];
    const typingSpeed = isDeleting ? 30 : 50;

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setDisplayText(currentText.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
        if (charIndex + 1 === currentText.length) {
          setTimeout(() => setIsDeleting(true), 1500);
        }
      } else {
        setDisplayText(currentText.substring(0, charIndex - 1));
        setCharIndex(charIndex - 1);
        if (charIndex === 0) {
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % industries.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, currentIndex, industries]);

  return (
    <div className="block text-5xl md:text-6xl lg:text-7xl industry-animation h-20">
      <span className="text-2xl sm:text-4xl lg:text-5xl text-ebony">for </span>
      <span className="typing-text">{displayText}</span>
      <span className={`cursor ${blink ? 'visible' : 'invisible'}`}>_</span>
    </div>
  );
});

const InteractiveCard = React.memo(({ data, delay = 0, align = 'left', onHoverChange }) => {
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true); // Start expanded
  const timerRef = useRef(null);

  const navigate = useNavigate();

  const handleHoverStart = () => {
    setIsHovered(true);
    setIsExpanded(true); // Force expand on hover
    if (timerRef.current) clearTimeout(timerRef.current);
    if (onHoverChange) onHoverChange(true);
  };

  const handleHoverEnd = () => {
    setIsHovered(false);
    if (onHoverChange) onHoverChange(false);
    // Cycle will resume via useEffect dependency on isHovered
  };

  const handleAction = useCallback((e) => {
    e.stopPropagation();
    const path = data[index].path || '/digital-services';
    navigate(path);
  }, [navigate, data, index]);

  useEffect(() => {
    if (isHovered) return;

    // Cycle Logic:
    // 1. Hold Expanded for 3s (reading time)
    // 2. Collapse (wait 500ms for transition)
    // 3. Switch Image
    // 4. Expand (wait 500ms for transition)

    let stepTimeout;

    const startCycle = () => {
      // Step 1: Start Retract (after hold)
      timerRef.current = setTimeout(() => {
        setIsExpanded(false);

        // Step 2: Switch Image (after collapse transition)
        stepTimeout = setTimeout(() => {
          setIndex((prev) => (prev + 1) % data.length);

          // Step 3: Expand back (immediately after switch)
          setIsExpanded(true);
        }, 500); // 500ms matching transition duration
      }, 4000 + (delay || 0)); // 4s hold + initial delay
    };

    // If just unhovered or component mounted, start the hold period
    // We remove 'delay' from subsequent cycles by managing it inside the recursive logic or simpler interval
    // Simpler approach: continuous loop

    // We need a recursive loop to handle the uneven timing (3s hold vs 0.5s transition)
    const runLoop = () => {
      timerRef.current = setTimeout(() => {
        setIsExpanded(false);

        stepTimeout = setTimeout(() => {
          setIndex((prev) => (prev + 1) % data.length);
          setIsExpanded(true);
          // Recursively call
          runLoop();
        }, 600); // Wait slightly longer than transition (500ms) to ensure fully closed
      }, 4000); // 4s Reading time
    };

    // Initial start with delay
    const initialTimer = setTimeout(() => {
      runLoop();
    }, delay);

    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(stepTimeout);
      clearTimeout(initialTimer);
    };
  }, [isHovered, data.length, delay]);


  return (
    <motion.div
      className={`relative flex items-center group cursor-pointer ${align === 'right' ? 'flex-row-reverse' : ''}`}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleHoverEnd}
      initial={{ width: 340, height: 260 }} // Start collapsed width conceptually, but animate to expanded
      animate={{
        width: isExpanded ? (window.innerWidth < 1024 ? 420 : 660) : 340,
        height: 260,
      }}
      transition={{ type: "spring", stiffness: 80, damping: 24, mass: 1.2 }}
      style={{ willChange: 'width, height' }}
    >
      {/* Premium Square Container - Image Holder */}
      <div
        className="z-20 relative flex-shrink-0 w-full max-w-[340px] h-[260px] rounded-[3rem] overflow-hidden border border-white/40 backdrop-blur-md bg-white/5 transition-all duration-500 will-change-transform"
        onClick={handleAction}
        role="button"
        aria-label={`View details about ${data[index].title}`}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={index}
            src={data[index].image}
            alt={data[index].alt || data[index].title}
            className="absolute inset-0 w-full h-full object-cover"
            width={340}
            height={280}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            fetchPriority={index === 0 ? "high" : "auto"}
            loading={index === 0 ? "eager" : "lazy"}
          />
        </AnimatePresence>

        {/* Architectural Overlays */}
        <div className="absolute inset-0 border-[0.5px] border-white/20 rounded-[3rem] pointer-events-none" />
      </div>

      {/* Elegant Expanding Content Panel */}
      <motion.div
        className={`absolute ${align === 'right' ? 'right-0 pr-[10%] sm:pr-[305px]' : 'left-0 pl-[10%] sm:pl-[305px]'} h-[260px] rounded-[3.5rem] overflow-hidden border border-white`}
        initial={{ opacity: 0, x: align === 'right' ? 50 : -50 }}
        animate={{
          opacity: isExpanded ? 1 : 0,
          x: isExpanded ? 0 : (align === 'right' ? 50 : -50), // Slide out/in effect
        }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        style={{ backgroundColor: '#FDFBF7', willChange: 'opacity, transform' }}
      >
        <div className={`p-8 sm:p-12 w-[320px] flex flex-col justify-center h-full ${align === 'right' ? 'text-right items-end' : ''}`}>
          <h3 className="text-hunter-green font-black text-2xl mb-3 tracking-tighter leading-none whitespace-nowrap">
            {data[index].title}
          </h3>
          <p className="text-ebony/70 text-sm leading-relaxed font-semibold line-clamp-3">
            {data[index].desc}
          </p>
          <div
            className="mt-6 flex items-center group/btn cursor-pointer"
            onClick={handleAction}
          >
            <span className="text-[10px] font-black text-muted-gold tracking-[0.3em] uppercase transition-all duration-300 group-hover/btn:tracking-[0.4em]">
              Discover More
            </span>
            <FiArrowRight className={`ml-2 text-muted-gold transition-transform duration-300 group-hover/btn:translate-x-1 ${align === 'right' ? 'order-first mr-2 ml-0 rotate-180 group-hover/btn:-translate-x-1' : ''}`} />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
});

const MobileHeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const carouselData = [
    {
      image: image5,
      title: 'Premium Stamping',
      desc: 'Seamless real-time digital stamp paper.'
    },
    {
      image: vendorImage,
      title: 'Vendor Partner',
      desc: 'Join our trusted vendor network.',
      path: '/vendor-onboarding'
    },
    {
      image: image6,
      title: 'Premium eSign',
      desc: 'Executive-level electronic signatures.'
    },
    {
      image: image7,
      title: 'Smart Docs',
      desc: 'Automated document generation.'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % carouselData.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full flex justify-center py-0 -mt-16">
      <div className="relative w-[300px] h-[300px] rounded-[2rem] overflow-hidden border border-gray-100 shadow-2xl bg-white">

        {/* Layer 1: Images (Cross-Fade) */}
        <AnimatePresence>
          <motion.div
            key={index}
            className="absolute inset-0 w-full h-full z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
          >
            <img
              src={carouselData[index].image}
              alt={carouselData[index].title}
              className="w-full h-full object-cover"
              fetchPriority={index === 0 ? "high" : "auto"}
              loading={index === 0 ? "eager" : "lazy"}
            />
          </motion.div>
        </AnimatePresence>

        {/* Layer 2: Static Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-20" />

        {/* Layer 3: Text (Synced Fade) */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white text-center z-30">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            >
              <h3 className="font-bold text-lg mb-1">{carouselData[index].title}</h3>
              <p className="text-xs opacity-90 font-medium">{carouselData[index].desc}</p>
            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </div>
  );
};

const Hero = ({ onRequestDemo }) => {
  const industries = useMemo(() => [
    'Banks', 'NBFCs', 'Stocks', 'Crypto', 'Corporates', 'Real Estate',
    'Logistics', 'Insurance', 'Legal Firms', 'Healthcare', 'Fintechs', 'and more'
  ], []);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);
  const [blink, setBlink] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isAnyCardHovered, setIsAnyCardHovered] = useState(false);

  const cursorBlinkRef = useRef(null);
  const typingTimeoutRef = useRef(null);



  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleContactClick = useCallback(() => onRequestDemo(), [onRequestDemo]);

  return (
    <>
      <section id="home" className="relative overflow-hidden bg-white min-h-[calc(108vh-80px)] flex flex-col -mt-[80px] pt-[80px]">
        {/* Interactive DotGrid Background */}
        {!isMobile && (
          <div className="absolute inset-0 z-10 opacity-30">
            <DotGrid
              dotSize={3}
              gap={40}
              baseColor="#5227FF"
              activeColor="#5227FF"
              proximity={120}
              shockRadius={250}
              shockStrength={5}
              resistance={750}
              returnDuration={1.5}
            />
          </div>
        )}

        {!isMobile && (
          <div className="absolute inset-0 overflow-hidden z-20 pointer-events-none">
            <motion.div
              animate={{ x: [0, -50, 0], y: [0, 25, 0] }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              className="absolute bottom-20 right-10 w-60 h-60 bg-gradient-to-r from-hunter-green/10 to-muted-gold/10 rounded-full blur-2xl"
            />
          </div>
        )}

        <div className="relative z-30 max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 sm:pt-8 pb-2 sm:pb-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-12 items-stretch">

            {/* Reverted Left Content Column */}
            <div className="flex flex-col justify-between h-auto lg:h-full lg:min-h-[560px] pt-2 sm:pt-4 pb-2 sm:pb-4">
              <div className="space-y-2">
                <div className="space-y-1">
                  <div className="flex justify-start lg:justify-start mb-1">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-hunter-green/10 to-muted-gold/10 border border-hunter-green/20">
                      <span className="text-xs sm:text-sm font-medium bg-gradient-to-r from-[#0047ab] to-[#1ca9c9] bg-clip-text text-transparent">
                        Leading <strong>Digital Document Solutions Across India</strong>
                      </span>
                    </div>
                  </div>

                  <h1 className="text-left pt-2 sm:pt-7 leading-tight space-y-1 mt-2">
                    <div className="block">
                      <span className="text-4xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-ebony">
                        Streamlined Document Solutions
                      </span>
                    </div>
                    <TypingHeading industries={industries} />
                  </h1>

                  {/* <div className="flex flex-wrap items-center gap-8 sm:gap-6 justify-center lg:justify-start !mt-6 sm:!mt-10">
                    <div className="flex items-center gap-3 sm:gap-4 group cursor-default">
                      <span className="text-[10px] sm:text-[12px] font-black uppercase tracking-[0.2em] text-ebony/60 leading-tight">Integral<br />Workflows</span>
                    </div>
                    <div className="text-ebony/30 text-xl font-light">|</div>
                    <div className="flex items-center gap-3 sm:gap-4 group cursor-default">
                      <span className="text-[10px] sm:text-[12px] font-black uppercase tracking-[0.2em] text-ebony/60 leading-tight">IT Act<br />Compliant</span>
                    </div>
                    <div className="text-ebony/30 text-xl font-light">|</div>
                    <div className="flex items-center gap-3 sm:gap-4 group cursor-default">
                      <span className="text-[10px] sm:text-[12px] font-black uppercase tracking-[0.2em] text-ebony/60 leading-tight">99.9% SLA<br />Uptime</span>
                    </div>
                  </div> */}
                </div>

                <div className="pt-4 sm:pt-6 mt-2 flex flex-col justify-start lg:justify-start gap-6 sm:gap-8">
                  <div className="flex items-center gap-4 flex-wrap">
                    <button
                      onClick={handleContactClick}
                      className="btn-shiny w-max"
                    >
                      <span className="text-xl">
                        Contact us
                        <FiArrowRight className="btn-shiny-icon inline ml-2" />
                      </span>
                    </button>

                    <a
                      href="/mat_final.pdf"
                      download="Matrica-Brochure.pdf"
                      className="btn-shiny-outline w-max"
                      aria-label="Download Matrica Brochure PDF"
                    >
                      <span className="text-xl">
                        Brochure
                        <FiDownload className="btn-shiny-outline-icon inline ml-2" />
                      </span>
                    </a>
                  </div>

                  <div className="inline-flex flex-wrap items-center gap-2 sm:gap-3 w-max pl-1">
                    <span className="text-[11px] sm:text-[13px] font-bold tracking-wider text-ebony/80 uppercase">Digital Stamping</span>
                    <span className="text-[13px] sm:text-[15px] text-muted-gold opacity-80 font-light">|</span>
                    <span className="text-[11px] sm:text-[13px] font-bold tracking-wider text-ebony/80 uppercase">Digital Storage</span>
                    <span className="text-[13px] sm:text-[15px] text-muted-gold opacity-80 font-light">|</span>
                    <span className="text-[11px] sm:text-[13px] font-bold tracking-wider text-ebony/80 uppercase">Smart Docs</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 sm:pt-6">
                <div className="flex flex-nowrap items-start gap-2 sm:gap-16 justify-between sm:justify-start">
                  <div className="flex flex-col items-center">
                    <div className="text-2xl sm:text-3xl font-bold stats-glow">35 Million+</div>
                    <div className="text-[10px] sm:text-sm text-ebony/60 uppercase tracking-widest font-black mt-2 text-center leading-tight">
                      Documents<br />Processed
                    </div>
                  </div>

                  <div className="flex flex-col items-center">
                    <div className="text-2xl sm:text-3xl font-bold stats-glow">26+</div>
                    <div className="text-[10px] sm:text-sm text-ebony/60 uppercase tracking-widest font-black mt-2 text-center leading-tight">
                      States & UTs<br />Covered
                    </div>
                  </div>

                  <div className="flex flex-col items-center">
                    <div className="text-2xl sm:text-3xl font-bold stats-glow">99.9%</div>
                    <div className="text-[10px] sm:text-sm text-ebony/60 uppercase tracking-widest font-black mt-2 text-center leading-tight">
                      Uptime<br />Rate
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Kept God-Level Architectural Right Side */}
            <div className="relative h-auto lg:h-[560px] flex items-center justify-center mt-12 lg:mt-0">
              {/* Distinct Background Container */}

              <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-transparent" />
              {/* Modern Grid Pattern */}
              <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '32px 32px' }} />


              {/* Content switching based on device */}
              {!isMobile ? (
                // Desktop Layout (Preserved God-Tier Logic)
                <div className="relative w-full h-auto lg:h-[560px] flex flex-col items-center px-4 sm:px-12 pt-4 pb-8">
                  <div className="w-full lg:w-[680px] lg:h-full flex flex-col justify-between items-center relative gap-8 lg:gap-6">
                    {/* Upper Block - Top Left */}
                    <div className="self-start lg:ml-[54px] relative z-40  transition-all duration-500">
                      <InteractiveCard
                        align="left"
                        isAutoExpanded={true}
                        onHoverChange={setIsAnyCardHovered}
                        data={[
                          {
                            image: image5,
                            apiName: 'StampSolv',
                            title: 'Stamping',
                            alt: 'Digital Stamp Paper Online India - Secure eStamp Procurement API',
                            desc: 'Seamless real-time digital stamp paper procurement and processing with material authenticity.'
                          },
                          {
                            image: vendorImage,
                            title: 'Vendor Partner',
                            alt: 'Matrica Vendor Partner Network - Handshake for Legal Compliance Service',
                            desc: 'Join our trusted vendor network and grow your business with our digital infrastructure.',
                            path: '/vendor-onboarding'
                          }
                        ]}
                      />
                    </div>

                    {/* Lower Block - Bottom Right */}
                    <div className="self-end relative z-40  transition-all duration-500">
                      <InteractiveCard
                        align="right"
                        isAutoExpanded={true}
                        onHoverChange={setIsAnyCardHovered}
                        delay={2000}
                        data={[
                          {
                            image: image6,
                            apiName: 'SignSolv',
                            title: 'eSign',
                            alt: 'Aadhaar eSign API Integration India - Secure Digital Signature Solution',
                            desc: 'Executive-level electronic signatures with absolute legal validity and premium execution.'
                          },
                          {
                            image: image7,
                            apiName: 'DoqSolv',
                            title: 'Smart Docs',
                            alt: 'Automated Document Generation API - Smart Legal Documentation Platform',
                            desc: 'Automated document generation and lifecycle management for enterprise workflows.'
                          }
                        ]}
                      />
                    </div>
                  </div>


                </div>
              ) : (
                // Mobile Layout (Single Square Block Carousel)
                <MobileHeroCarousel />
              )}
            </div>
          </div>
        </div>
      </section >

      <style>{`
          .typing-text {
            background: linear-gradient(90deg, #004F30, #1fc40fff, #004F30);
            background-size: 200% 100%;
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            font-weight: 700;
          }

          .cursor {
            color: #004F30;
            font-weight: 700;
          }

          .stats-glow {
           background: linear-gradient(
                  to right,
                  #de751eff,
                  #cd6d14ff,
                  #f58e27ff
                );


            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
          }

          /* Animations only for desktop */
          @media (min-width: 768px) {
            .typing-text {
              animation: gradient-flow 8s ease-in-out infinite;
            }
            
            .stats-glow {
              animation: shimmer 4s ease-in-out infinite;
            }
          }

          @keyframes gradient-flow {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }

          @keyframes shimmer {
            0%, 100% { filter: brightness(1); }
            50% { filter: brightness(1.2); }
          }

          @media (prefers-reduced-motion: reduce) {
            .typing-text, .stats-glow {
              animation: none;
            }
          }
        `}</style>
    </>
  );
};

export default Hero;
